import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Box, Button, Stack, SxProps, Text } from '@ltvco/refresh-lib/theme';
import { usePhoneFormatter, ValidReportTypes } from '@ltvco/refresh-lib/utils';
import { IconContainer } from '@ltvco/refresh-lib/v2';
import {
  AlternateEmail,
  DirectionsCar,
  Email,
  Home,
  Language,
  LocalFlorist,
  MapsHomeWork,
  Money,
  Person,
  PhoneInTalk,
  PhoneIphone,
  FamilyRestroom,
  Work,
} from '@mui/icons-material';
import { ReactNode } from 'react';

type ReportCardProps = {
  icon?: ReactNode;
  title: string | ReactNode;
  description?: string | ReactNode;
  reportType: ValidReportTypes;
  clickAction?: () => void;
  actionLabel?: string;
  actionPosition?: 'right' | 'bottom' | 'hidden';
  actionVariant?: 'contained' | 'text';
  containerStyles?: SxProps;
  iconStyles?: SxProps;
  titleStyles?: SxProps;
  descriptionStyles?: SxProps;
  actionStyles?: SxProps;
  stackInMobile?: boolean;
  disabled?: boolean;
};

export const ReportChatCard: React.FC<ReportCardProps> = ({
  icon: customIcon,
  title,
  description,
  reportType,
  clickAction,
  actionLabel,
  actionPosition = 'right',
  containerStyles,
  iconStyles,
  titleStyles,
  descriptionStyles,
  actionStyles,
  actionVariant = 'text',
  stackInMobile,
  disabled,
}) => {
  const { internationalPhoneInput } = useAppConstantsContext();

  const ICONSBYREPORT = {
    person: <Person color="primary" fontSize="small" />,
    phone: <PhoneIphone color="primary" fontSize="small" />,
    email: <Email color="primary" fontSize="small" />,
    property: <Home color="primary" fontSize="small" />,
    vehicle: <DirectionsCar color="primary" fontSize="small" />,
    username: <AlternateEmail color="primary" fontSize="small" />,
    neighborhood: <MapsHomeWork color="primary" fontSize="small" />,
    obituary: <LocalFlorist color="primary" fontSize="small" />,
    contact: <Work color="primary" fontSize="small" />,
    ancestor: <FamilyRestroom color="primary" fontSize="small" />,
    sex_offender: <Person color="primary" fontSize="small" />,
    'unclaimed-money': <Money color="primary" fontSize="small" />,
    'phone-fraud': <PhoneInTalk color="primary" fontSize="small" />,
    'email-fraud': <Email color="primary" fontSize="small" />,
    'ip-fraud': <Language color="primary" fontSize="small" />,
    'url-fraud': <Language color="primary" fontSize="small" />,
  };

  function getIconByReportType(reportType: string) {
    return ICONSBYREPORT[reportType as keyof typeof ICONSBYREPORT];
  }

  const formattedPhoneNumber = usePhoneFormatter(
    title as string,
    internationalPhoneInput?.enabled
  );

  const shouldFormatPhone =
    reportType === 'phone' || reportType === 'phone-fraud';
  const formattedReportTitle = shouldFormatPhone ? formattedPhoneNumber : title;

  function onCardClick() {
    if (!clickAction || disabled) {
      return;
    }
    clickAction();
  }

  const icon = customIcon || getIconByReportType(reportType);

  return (
    <Stack
      px={1.5}
      py={2}
      sx={{
        backgroundColor: (theme) => theme.palette.background.paper,
        ':hover': {
          backgroundColor: (theme) => theme.palette.background.default,
          cursor: clickAction ? 'pointer' : 'auto',
        },
        ...containerStyles,
      }}
      onClick={onCardClick}
    >
      <Stack direction={stackInMobile ? 'column' : 'row'} spacing={1.5}>
        {icon ? <IconContainer icon={icon} sx={iconStyles} /> : null}
        <Stack
          spacing={0.5}
          width={
            actionPosition === 'right'
              ? 'calc(100% - 118px)'
              : 'calc(100% - 60px)'
          }
        >
          <Text
            variant="h5"
            sx={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              lineHeight: 1.375,
              ...titleStyles,
            }}
          >
            {formattedReportTitle}
          </Text>
          <Box
            sx={{
              wordBreak: 'break-word',
              color: 'text.secondary',
              ...descriptionStyles,
              // fix for smaller devices,
              // allows the card to look as other reports cards
              ...(reportType === 'ancestor' && {
                wordBreak: 'normal',
              }),
            }}
          >
            {description}
          </Box>
        </Stack>
        {actionPosition === 'right' && (
          <Button
            variant={actionVariant}
            sx={{
              width: stackInMobile ? '100%' : 66,
              height: 46,
              ...actionStyles,
            }}
            disabled={disabled}
          >
            {actionLabel || 'View'}
          </Button>
        )}
      </Stack>
      {actionPosition === 'bottom' && (
        <Button
          variant={actionVariant}
          sx={{ width: '100%', height: 36, marginTop: 2, ...actionStyles }}
          disabled={disabled}
        >
          {actionLabel || 'View'}
        </Button>
      )}
    </Stack>
  );
};
